<template>
  <!-- 상품 정보 -->
  <div class="payments-page-wrap tw:flex tw:flex-col tw:gap-[10px]!">
    <section class="payment-order-section">
      <div class="product-info">
        <div class="icon-wrap">
          <nuxt-img
            class="flag"
            :src="'/img/insurance-payments-prod-info.jpg'"
            alt="insurance-payments-prod-info"
            width="56"
            height="56"
          />
        </div>
        <ul class="summary">
          <li>
            {{ checkoutData.destinationCountryName }}
          </li>
          <li>해외여행보험 {{ checkoutData.planName }} 플랜</li>
          <li class="summary-caption">
            {{ fmtDateTime(checkoutData.insuranceStartDateTime) }}
            -
            {{ fmtDateTime(checkoutData.insuranceEndDateTime) }}
          </li>
        </ul>
      </div>

      <ul class="subscriber">
        <li class="subscriber-wrap">
          <span>계약자</span>
          <span class="subscriber-caption">{{ checkoutData.contractorName }}</span>
        </li>
        <li class="subscriber-wrap">
          <span>보장받는 분</span>
          <span class="subscriber-caption"
            >{{ checkoutData.contractorName }}
            <span v-if="checkoutData.insuredCount - 1 > 0"
              >외 {{ checkoutData.insuredCount - 1 }}명</span
            >
          </span>
        </li>
      </ul>
    </section>

    <!-- 결제 수단 -->
    <TossPayments
      ref="tossPaymentRef"
      :price="checkoutData.premium"
      :user-id="userId ?? ''"
      :item-uid="props.checkoutData.orderGroupId ?? ''"
      success-path="/insurance/purchase/callback/success"
      fail-path="/insurance/purchase/callback/fail"
      variant="usdg"
    />

    <!-- 결제 금액 -->
    <section class="payments-amount-summary">
      <h3 class="order-section__title">결제금액</h3>

      <ul class="payment-ul">
        <li class="payment-list">
          <span>상품 금액</span>
          <span>{{ commaWon(checkoutData.subtotalPremium) }}</span>
        </li>
        <li
          v-if="groupDiscountLabel"
          class="payment-list"
        >
          <span>{{ groupDiscountLabel }}</span>
          <span> -{{ commaWon(checkoutData.discountAmount) }}</span>
        </li>
      </ul>
      <div class="payment-label">
        <span>총 결제 금액</span>
        <span>{{ commaWon(checkoutData.premium) }}</span>
      </div>
    </section>
  </div>

  <!-- 결제하기 버튼 -->
  <BtnPayment
    :is-loading="isProcessingPayment"
    :disabled="isDisableBtnPayment"
    :final-price="checkoutData.premium"
    @click="handlePaymentsBtn"
    variant="usdg"
  />
</template>

<script setup lang="ts">
import type { InsuranceCheckoutData } from '@/uTypes/order/insurance'

const props = defineProps<{
  checkoutData: InsuranceCheckoutData
}>()

const { userData } = storeToRefs(useUserData())
const userId = computed(() => userData.value?.insideId ?? '')
const { commaWon } = useUi()
const dayjs = useDayjs()

const fmtDateTime = (iso: string | null) => (iso ? dayjs(iso).format('YY.MM.DD HH시') : '')

// TOSS
const tossPaymentRef = ref()

const { isDisabledByPaymentState, canProceedPayment } = usePaymentButtonState(tossPaymentRef)
const isDisableBtnPayment = computed(() => isDisabledByPaymentState(true))

const { isProcessingPayment } = usePayment()

const handlePaymentsBtn = async () => {
  if (isProcessingPayment.value) return
  if (!canProceedPayment(true)) return

  isProcessingPayment.value = true
  try {
    const paymentData: PaymentData = {
      orderId: props.checkoutData.orderGroupId,
      orderGroupId: props.checkoutData.orderGroupId,
      orderName: `KAKAO INSURANCE - ${props.checkoutData.planName}`,
      amount: props.checkoutData.premium,
      queryString: '',
      customerName: props.checkoutData.contractorName ?? '',
      customerMobilePhone: ''
    }

    await tossPaymentRef.value?.requestTossPayment(paymentData)
  } catch (error) {
    console.error('Error:', error)
    isProcessingPayment.value = false
  }
}

const groupDiscountLabel = computed(() => {
  const subscriberCount = props.checkoutData.insuredCount

  if (subscriberCount >= 3) {
    return '함께 가입 할인 10%'
  }

  if (subscriberCount >= 2) {
    return '함께 가입 할인 5%'
  }

  return ''
})

onMounted(() => {
  isProcessingPayment.value = false
})
</script>
<style scoped lang="scss">
:deep(.order-section) {
  padding: 16px;

  .order-section__title {
    padding-bottom: 16px;
    font-size: 18px;
    font-weight: 600;
    line-height: 28px;
  }
}

.payments-page-wrap {
  padding: 0 16px;
}

.payment-order-section {
  margin-top: 66px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-radius: 20px;
  border: 0.5px solid $usdg-color-gray-300;
  background-color: white;

  .product-info {
    display: flex;
    gap: 10px;
    border-bottom: 1px solid #e5e5ec;

    .icon-wrap {
      width: 72px;
      height: 72px;
      padding: 8px;
    }

    .summary {
      font-size: 16px;
      font-weight: 600;
      line-height: 24px;
      color: $usdg-color-gray-900;
      padding-bottom: 16px;
      padding-left: 0px;

      .summary-caption {
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        color: $usdg-color-gray-800;
        padding-top: 2px;
      }
    }
  }

  .subscriber {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: #222222;
    .subscriber-wrap {
      display: flex;
      justify-content: space-between;
    }

    .subscriber-caption {
      text-align: right;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
    }
  }
}
.payment-order-section ul,
.payment-order-section li {
  list-style: none;
}
.payments-amount-summary {
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  border: 0.5px solid $usdg-color-gray-300;
  background-color: white;
  padding: 16px 14px;
  gap: 14px;
  color: #222222;
  margin-bottom: 160px;

  .payment-ul {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-bottom: 16px;
    border-bottom: 1px solid $stroke-default;
    padding-left: 0px;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;

    .payment-list {
      display: flex;
      justify-content: space-between;
    }
  }

  .payment-label {
    display: flex;
    justify-content: space-between;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
  }
}

.payments-amount-summary ul,
.payments-amount-summary li {
  list-style: none;
}
</style>
